import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  untracked,
  viewChild,
  WritableSignal
} from '@angular/core';
import { DropdownListComponent, MultiselectItem, Status } from '@ui/aos-lib';
import { map } from 'rxjs';
import { DeviceBase } from 'src/app/services/data/devices-data/devices-data.types';
import { FilterModel } from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model.types';
import {
  DeviceCardComponent,
  type DeviceForCard
} from '../../../../equipment/devices/components/device-card/device-card.component';
import { HealthDetails } from '../../../../equipment/devices/components/devices.models';
import type { CustomerAssociatedDevice } from '../../customer-devices/customer-devices.type';
import { type FullAccountNumber } from '../../customers.types';
import { CustomerDetailsService } from '../../services/customer-details.service';
import { TranslateModule } from '@ngx-translate/core';
import { TranslateService } from 'src/app/services/translate/translate.service';
import { TranslateRegistry } from 'src/app/services/translate/translate.registry';

@Component({
  selector: 'aos-customer-side-panel',
  imports: [DeviceCardComponent, DropdownListComponent, AsyncPipe, TranslateModule],
  providers: [CustomerDetailsService],
  templateUrl: './customer-side-panel.component.html',
  styleUrl: './customer-side-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomerSidePanelComponent {
  private readonly translate = inject(TranslateService);

  readonly deviceCardData = input.required<
    Array<{ readonly associatedDevices: readonly CustomerAssociatedDevice[] }> | undefined
  >();
  readonly basicData = input();
  readonly customerCardData = input();

  readonly bodyElement = viewChild('sidePanelBody', { read: ElementRef });

  readonly #customerDetailsService: CustomerDetailsService = inject(CustomerDetailsService);

  accountNumbersFilterModel: WritableSignal<Omit<FilterModel, 'filterType'>> = signal({
    title: TranslateRegistry.get.FILTER.TITLE.ACCOUNT_NUMBER,
    selected: [{ text: TranslateRegistry.get.FILTER.COMMON.ALL_SELECTED, value: null }],
    items: [{ text: TranslateRegistry.get.FILTER.COMMON.ALL_SELECTED, value: null }],
    width: 180,
    isMultiselect: false
  });

  deviceStatusFilterModel: Omit<FilterModel, 'filterType'> = {
    title: TranslateRegistry.get.FILTER.TITLE.DEVICE_STATUS,
    selected: [{ text: TranslateRegistry.get.FILTER.STATUS.ACTIVE, value: 'Active' }],
    items: [
      { text: TranslateRegistry.get.FILTER.COMMON.ALL_SELECTED, value: null },
      { text: TranslateRegistry.get.FILTER.STATUS.ACTIVE, value: 'Active' },
      { text: TranslateRegistry.get.FILTER.STATUS.INACTIVE, value: 'Inactive' },
      { text: TranslateRegistry.get.FILTER.STATUS.REMOVED, value: 'Removed' }
    ],
    width: 180,
    isMultiselect: false
  };

  protected readonly accountNumbersFilterModelTranslated = computed(() =>
    this.translate.getTranslatedItems(this.accountNumbersFilterModel().items)
  );
  protected readonly deviceStatusFilterModelTranslated = computed(() =>
    this.translate.getTranslatedItems(this.deviceStatusFilterModel.items)
  );

  protected filteredAssociatedDevicesCardData$ = this.#customerDetailsService.filteredAssociatedDevicesCardData$.pipe(
    map(devices => {
      return devices.map((device: CustomerAssociatedDevice): DeviceForCard => {
        return {
          ...device,
          associatedDevices: devices.filter(d => d.deviceUID !== device.deviceUID) as unknown as DeviceBase[],
          // FIXME: Check the types, organize mapping.
          healthDetails: [...device.healthDetails] as unknown[] as HealthDetails[]
        };
      });
    })
  );

  protected associatedDevicesTitle = TranslateRegistry.get.DEVICE_DETAILS.ASSOC_SIDE_CARD.ASSOCIATED_DEVICES;
  private currentDeviceStatus: Status | undefined = (this.deviceStatusFilterModel.selected[0]?.value as Status) ?? null;
  private currentAccountNumber: FullAccountNumber | null = null;

  constructor() {
    effect(() => {
      const deviceCardData = this.deviceCardData();
      if (deviceCardData?.length) {
        this.currentAccountNumber = null;

        const [{ associatedDevices }] = deviceCardData;
        const accountNumbersFilterModel = untracked(() => this.accountNumbersFilterModel());

        this.accountNumbersFilterModel.set({
          ...accountNumbersFilterModel,
          items: this.#customerDetailsService.createAccountNumbersFilterItems(
            associatedDevices,
            accountNumbersFilterModel.selected
          )
        });

        this.#customerDetailsService.filterCustomerDevices(associatedDevices, null, this.currentDeviceStatus);

        this.scrollToContentTop();
      }
    });
  }

  onAccountNumberChange(event: MultiselectItem): void {
    const [{ associatedDevices }] = this.deviceCardData()!;

    if (event.value) {
      const accountData = event.value?.toString().split('/');
      this.currentAccountNumber = {
        accountNumber: accountData[0],
        accountSubNumber: accountData[1]
      };
    } else {
      this.currentAccountNumber = null;
    }

    this.#customerDetailsService.filterCustomerDevices(
      associatedDevices,
      this.currentAccountNumber,
      this.currentDeviceStatus
    );
  }

  onDeviceStatusChange(event: MultiselectItem): void {
    this.currentDeviceStatus = event.value as Status;
    const [{ associatedDevices }] = this.deviceCardData()!;

    this.#customerDetailsService.filterCustomerDevices(
      associatedDevices,
      this.currentAccountNumber,
      this.currentDeviceStatus
    );
  }

  private scrollToContentTop(): void {
    this.bodyElement()?.nativeElement.scrollTo(0, 0);
  }
}
